import type { ReactNode } from 'react';
import type { CareDto } from '../api/care';
import { HeartGlyph } from '../shell/glyphs';

type CareStatKey = 'hunger' | 'joy' | 'grooming';

const STATS: { key: CareStatKey; label: string }[] = [
  { key: 'hunger', label: 'Hunger' },
  { key: 'joy', label: 'Joy' },
  { key: 'grooming', label: 'Grooming' },
];

// SCREENS.md /habitat "low-care-cue": <30 on any stat. Exported so HabitatScreen's CompanionCard
// can apply `.compositor-dull` to its own live pet (the Pet3DStage, rendered elsewhere in the card,
// not by this component) from the exact same rule this component uses for its own warm note below.
export const LOW_CARE_THRESHOLD = 30;

// C1/C3: this note is an invitation, never a report of harm. NEVER "is starving"/"is sad"/
// "hasn't been fed in a while" — no guilt, no decay/loss framing, ever (SCREENS.md low-care-cue:
// "copy stays warm — NEVER guilt messaging, never implies decay/loss").
const LOW_CARE_COPY: Record<CareStatKey, (name: string) => string> = {
  hunger: (name) => `${name} could use a snack.`,
  joy: (name) => `${name} could use a play.`,
  grooming: (name) => `${name} could use a brush-up.`,
};

export type LowCareCue = { key: CareStatKey; note: string };

/**
 * The lowest care stat below `LOW_CARE_THRESHOLD`, or `null` when every stat clears it. Ties
 * break in a fixed order (hunger, then joy, then grooming) so the cue is deterministic rather
 * than depending on object key iteration order.
 */
export function lowCareCue(care: CareDto, companionName: string): LowCareCue | null {
  let lowest: { key: CareStatKey; value: number } | null = null;
  for (const { key } of STATS) {
    const value = care[key];
    if (value < LOW_CARE_THRESHOLD && (lowest === null || value < lowest.value)) {
      lowest = { key, value };
    }
  }
  return lowest ? { key: lowest.key, note: LOW_CARE_COPY[lowest.key](companionName) } : null;
}

// 24-unit grid, 2-unit stroke, currentColor — same convention as shell/glyphs.tsx's own
// top-of-file comment. Kept local (not added to that shared file) since these three are specific
// to care meters, aria-hidden here since each meter already carries its own text label.
function HungerGlyph(): ReactNode {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
      <path
        d="M12 8c-3 0-5.5 2.5-5.5 6a6 6 0 0 0 6 6c1 0 1.7-.3 2-.5.3.2 1 .5 2 .5a6 6 0 0 0 6-6c0-3.5-2.5-6-5.5-6-.8 0-1.5.2-2 .5-.5-.3-1.2-.5-2-.5z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path d="M12 8V5c0-1 .7-2 2-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function JoyGlyph(): ReactNode {
  return <HeartGlyph filled />;
}

function GroomingGlyph(): ReactNode {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
      <path
        d="M12 3v4M12 17v4M3 12h4M17 12h4M6 6l2.5 2.5M15.5 15.5 18 18M18 6l-2.5 2.5M8.5 15.5 6 18"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

const GLYPH: Record<CareStatKey, () => ReactNode> = {
  hunger: HungerGlyph,
  joy: JoyGlyph,
  grooming: GroomingGlyph,
};

export type CareMetersProps = {
  care: CareDto;
  /** For the low-care note's copy ("Bramble could use a snack.") — never used to recompute
   *  `thriving`, which is always the server's own `care.thriving` flag (care.ts: "true only when
   *  all three are >= 70" — this component never re-derives that threshold itself). */
  companionName: string;
  /** When false the Thriving badge is not rendered here and the caller places it itself.
   *  HabitatScreen does this so the three meters get a full single row in the compact identity
   *  bar; every other caller (the gallery) keeps the default in-row badge. */
  showThriving?: boolean;
};

/**
 * SCREENS.md /habitat care additions: three labelled meters (icon + visible text label + a
 * token-based fill bar, `role="meter"`/`aria-valuenow` for assistive tech — §11.8 "never colour
 * alone," so every value is also plain visible text, never just a filled bar), the Thriving
 * badge, and the low-care warm note (see `lowCareCue` above).
 */
export function CareMeters({ care, companionName, showThriving = true }: CareMetersProps) {
  const cue = lowCareCue(care, companionName);

  return (
    <div className="care-meters">
      <div className="care-meters-row">
        {STATS.map(({ key, label }) => {
          const value = care[key];
          const Glyph = GLYPH[key];
          return (
            <div
              key={key}
              className="care-meter"
              role="meter"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={value}
              aria-label={label}
            >
              <Glyph />
              <span className="care-meter-label">{label}</span>
              <div className="care-meter-track" aria-hidden="true">
                <div className="care-meter-fill" style={{ width: `${value}%` }} />
              </div>
              <span className="care-meter-value">{value}</span>
            </div>
          );
        })}
        {showThriving && care.thriving && (
          <span className="badge-thriving" aria-label="Thriving: +10% XP and better finds from battles, gathering and gifts">
            Thriving · +10%
          </span>
        )}
      </div>
      {cue && <p className="care-low-note">{cue.note}</p>}
    </div>
  );
}
